/**
 * Code fallback for CMS content. The DB is the source of truth at runtime;
 * these defaults exist only so the public site never shows blank during a
 * cold start or if the DB is wiped/migrated. Keep these in sync with
 * /app/backend/cms_data.py DEFAULT_BLOCKS for graceful degradation.
 */

export const CMS_FALLBACK = {
  home: {
    "home.hero.badge": "Kid-run · Parent-supervised",
    "home.hero.title_lead": "When valet trash takes a break,",
    "home.hero.title_brand": "Bag Dash Kids",
    "home.hero.title_tail": "makes the dash.",
    "home.hero.paragraph":
      "Two young entrepreneurs (ages 12 & 9) pick up your tied trash bags on the days valet trash isn't running. Easy to book, kind on your wallet, and totally parent-supervised.",
    "home.hero.cta_primary": { label: "Schedule Pickup", href: "/schedule" },
    "home.hero.cta_secondary": { label: "Service Rules", href: "/rules" },
    "home.hero.chips": [
      "Fri AM · Fri PM · Sat AM · Sat PM",
      "Secure Stripe payments",
      "Earn Dash Rewards",
    ],
    "home.pricing.title": "Simple, kid-friendly pricing",
    "home.pricing.subtitle": "Pay only for the bags you have.",
    "home.pricing.one_bag_label": "1 bag",
    "home.pricing.two_bag_label": "2 bags",
    "home.pricing.extra_note": "Got more bags? +${extra_per_bag} each beyond two.",
    "home.pricing.addon_note": "Monthly bin cleaning for loyal customers — ${bin_clean_price}.",
    "home.pricing.cta": { label: "Schedule a Pickup", href: "/schedule" },
    "home.how.title": "How it works",
    "home.how.subtitle": "Three steps. No drama. Bags gone.",
    "home.how.steps": [
      { title: "Schedule", body: "Pick Friday AM, Friday PM, Saturday AM, or Saturday PM. One-time or weekly." },
      { title: "Pay Securely", body: "Stripe Checkout. We never store your card. Pay only after confirming." },
      { title: "We Dash", body: "Place tied bags outside at your spot. The kids handle the rest." },
    ],
    "home.rewards.eyebrow": "Dash Rewards",
    "home.rewards.title": "Loyal customers get the good stuff.",
    "home.rewards.body":
      "Earn 1 Dash Point per paid pickup. Recurring weekly? +1 bonus. Refer a neighbor? +1 each. Four points unlocks a free pickup, discounted bin cleaning, or a priority pickup window.",
    "home.rewards.cta": { label: "Start earning points", href: "/register" },
    "home.rewards.tiles": [
      "Free 1-bag pickup",
      "Bin cleaning discount",
      "Priority pickup window",
      "Loyalty badge",
    ],
    "home.cta.title": "Ready to skip a trash run?",
    "home.cta.body":
      "Schedule in under a minute. Cancel anytime. Family-run, neighbor-approved.",
    "home.cta.button": { label: "Schedule a Pickup", href: "/schedule" },
  },
  about: {
    "about.title": "Who we are",
    "about.intro":
      "We're **Bag Dash Kids** — two young entrepreneurs (ages 12 and 9) making apartment life easier, *one bag at a time*. When valet trash takes a break, we make the dash. Family-run, parent-supervised, and proudly local.",
    "about.values": [
      { title: "We dash", body: "Fri AM · Fri PM · Sat AM · Sat PM" },
      { title: "We care", body: "Tied bags only. Outside only. Always safe." },
      { title: "We build", body: "Growing one community at a time." },
    ],
    "about.team_title": "Meet the team",
    "about.team_note": "Animated placeholders — real team photos coming soon!",
    "about.team_members": [
      { title: "Big Sib (12)", body: "Lead Dasher" },
      { title: "Lil Sib (9)", body: "Bag Whisperer" },
      { title: "Mom (Boss)", body: "Parent & Supervisor" },
    ],
  },
  rules: {
    "rules.title": "Service Rules",
    "rules.subtitle": "Keeping our kid workers safe and your service smooth.",
    "rules.allowed": [
      "Tied household trash bags",
      "Bags placed outside in the agreed location",
      "Reasonable, normal-sized bags",
    ],
    "rules.not_allowed": [
      "Hazardous waste (paint, chemicals, batteries)",
      "Broken glass (unless safely boxed and labeled)",
      "Sharp items (no needles, blades)",
      "Leaking bags",
      "Loose, untied trash",
      "Oversized items (furniture, big boxes)",
    ],
    "rules.safety": [
      "The kids do not enter homes, garages, or private indoor spaces.",
      "All pickups happen outside in the agreed location only.",
      "A parent/guardian oversees the business and service schedule.",
      "If anything looks unsafe, the kids will skip the pickup and you'll be notified.",
    ],
  },
  policies: {
    "policies.title": "Customer Policies & FAQ",
    "policies.subtitle": "Everything you need to know about how Bag Dash Kids works.",
    "policies.sections": [
      {
        title: "Weekly subscription — billing & cancellation",
        bullets: [
          "**How billing works:** Your card is charged automatically by Stripe every week on the same day your subscription started. We never store your card — Stripe handles it securely.",
          "**What you're charged:** $3/week for 1 bag, $5/week for 2 bags. Tips and the optional monthly bin cleaning add-on are billed as separate one-time charges.",
          "**How to cancel:** Go to **My Plan** in your dashboard and click **Cancel**. Cancelling stops all future weekly charges immediately. You'll keep service through the end of the current week if it's already been picked up.",
          "**How to pause instead:** Use the **Pause** button on My Plan if you're going on vacation. We'll skip your pickups and not charge you while paused. Resume anytime in one tap.",
          "**Refunds:** If we miss a pickup we already charged you for, contact the parent and we'll refund or credit the week. We don't refund for skipped weeks where bags weren't placed outside in time.",
          "**Changing windows / bags:** Edit your subscription from My Plan. Changes take effect next billing cycle.",
          "**Receipts:** Every weekly charge sends an email receipt to the address on file. Need an old one? Ask the parent — they can resend any receipt from the admin dashboard.",
        ],
      },
      {
        title: "Pricing & payments",
        bullets: [
          "1 bag: **$3** · 2 bags: **$5** · Each extra bag: **+$2**.",
          "Monthly bin cleaning add-on: **$10** (free for high-loyalty customers).",
          "We accept Apple Pay, Google Pay, and all major cards via Stripe Checkout.",
          "**Pay-in-person** is available — cash or check at pickup. The parent marks the order paid after.",
        ],
      },
      {
        title: "Service days & cutoffs",
        bullets: [
          "We run Friday Morning, Friday Evening, Saturday Morning, and Saturday Evening windows.",
          "Booking closes the moment a window starts. If you miss the cutoff, pick the next window.",
          "Bags must be tied and placed outside before the window begins.",
        ],
      },
      {
        title: "Safety & what we won't pick up",
        bullets: [
          "No hazardous waste, sharp objects, leaking bags, or oversized items.",
          "The kids never enter homes, garages, or private indoor spaces.",
          "A parent/guardian oversees the business and supervises all pickups.",
        ],
      },
    ],
  },
  contact: {
    "contact.title": "Contact & FAQ",
    "contact.form_title": "Send us a message",
    "contact.form_placeholder": "How can we help?",
    "contact.faq_title": "Frequently asked",
    "contact.faq": [],
  },
  global: {
    "global.logo.url": { url: "/logo.png", alt: "Bag Dash Kids" },
    "global.brand_name": "Bag Dash Kids",
    "global.footer.tagline":
      "Family-run, parent-supervised. Making apartment life easier, one bag at a time.",
    "global.footer.col_explore_title": "Explore",
    "global.footer.col_explore": [
      { title: "Schedule", bullets: ["/schedule"] },
      { title: "Service areas", bullets: ["/map"] },
      { title: "Reviews", bullets: ["/reviews"] },
      { title: "Leaderboard", bullets: ["/leaderboard"] },
    ],
    "global.footer.col_help_title": "Help",
    "global.footer.col_help": [
      { title: "Service rules", bullets: ["/rules"] },
      { title: "Policies & FAQ", bullets: ["/policies"] },
      { title: "About us", bullets: ["/about"] },
      { title: "Contact / FAQ", bullets: ["/contact"] },
    ],
    "global.footer.copyright_suffix": "Tied bags only · No hazardous waste",
  },
  seo: {
    "seo.title": "Bag Dash Kids | Trash Pickup Service",
    "seo.description":
      "Bag Dash Kids — friendly neighborhood trash bag pickup by kid entrepreneurs. Schedule one-time or weekly pickups.",
  },
};
